import * as d3 from 'd3';
import {
  interpolateInferno,
  interpolatePlasma,
  interpolateViridis,
} from 'd3-scale-chromatic';

type Selection = d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>;

/**
 * Available colormaps for mask display.
 * Shared between the mask component and its colorbar UI.
 */
export const MASK_COLORMAPS: Record<string, (t: number) => string> = {
  plasma: interpolatePlasma,
  viridis: interpolateViridis,
  inferno: interpolateInferno,
};

export interface MaskPixelData {
  values: number[];
  coords: [number, number][]; // [row, col] pairs
}

/**
 * Quantise a 0-1 value into `steps` discrete buckets.
 * Returns an integer in [0, steps-1].
 */
function quantise(t: number, steps = 256): number {
  return Math.min(steps - 1, Math.max(0, Math.floor(t * steps)));
}

/**
 * Draw all mask pixels as a minimal number of `<path>` elements.
 *
 * Pixels are grouped by quantised colour so that every pixel with the
 * same (discretised) fill shares a single compound path.  This keeps
 * the DOM node count tiny (≤ 256) regardless of the pixel count.
 *
 * Each pixel is a closed rect subpath: `M x,y h1 v1 h-1 z`.
 *
 * All coordinates are in image-pixel space; the parent `<g>` carries
 * the image→screen transform (applied by `applyGroupTransform`).
 *
 * @param group   - D3 selection of the parent `<g>` element
 * @param data    - mask pixel data (values + [row, col] coords)
 * @param min     - scalar range minimum (for normalisation)
 * @param max     - scalar range maximum
 * @param colormap - d3 interpolator function (e.g. `interpolatePlasma`)
 * @param opacity - fill opacity (default 1)
 * @param pixelSize - size of each pixel quad in image units (default 1)
 */
export function appendPixelGrid(
  group: Selection,
  data: MaskPixelData,
  min: number,
  max: number,
  colormap: (t: number) => string,
  opacity = 1,
  pixelSize = 1,
): void {
  if (!data || !data.values || !data.coords || data.values.length === 0) return;

  const { values, coords } = data;
  const range = max - min || 1;

  // Bucket: quantised-colour-index → { hex colour, SVG path fragments }
  const buckets = new Map<number, { hex: string; parts: string[] }>();

  for (let i = 0; i < coords.length; i++) {
    const [y, x] = coords[i]; // data stores [row, col]
    const t = Math.max(0, Math.min(1, (values[i] - min) / range));
    const q = quantise(t);

    let bucket = buckets.get(q);
    if (!bucket) {
      bucket = { hex: colormap(t), parts: [] };
      buckets.set(q, bucket);
    }
    // Compact SVG rect subpath
    bucket.parts.push(`M${x},${y}h${pixelSize}v${pixelSize}h${-pixelSize}z`);
  }

  // One <path> per colour bucket
  for (const { hex, parts } of buckets.values()) {
    group
      .append('path')
      .attr('d', parts.join(''))
      .attr('fill', hex)
      .attr('opacity', opacity)
      .attr('stroke', hex)
      .attr('stroke-width', 0.5)
      .attr('stroke-linejoin', 'round')
      .attr('vector-effect', 'non-scaling-stroke');
  }
}
